import { Link, Navigate, useParams } from "react-router-dom";
import styles from "./BlogArticle.module.scss";
import { blogArticles, blogArticleList } from "../data/blogArticles";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { ArticleHeader } from "../components/BlogArticle/ArticleHeader";
import { TableOfContents } from "../components/BlogArticle/TableOfContents";
import { ArticleContent } from "../components/BlogArticle/ArticleContent";
import { Sidebar } from "../components/BlogArticle/Sidebar";
import { BlogMedia } from "../components/BlogArticle/InstagramEmbed";
import { trackCtaClick } from "../utils/analytics";
import { useLanguage } from "../contexts/LanguageContext";
import {
  localizeArticle,
  localizeArticleList,
} from "../utils/localizedContent";
import { blogText } from "../i18n/blog";
import BlogMemoryCta from "../components/BlogMemoryCta/BlogMemoryCta";
import { getBlogGuideLinks } from "../data/seoContentClusters.mjs";

export default function BlogArticle() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = blogText[lang] || blogText.JP;
  const rawArticle = blogArticles?.[slug];

  if (!rawArticle) {
    return <Navigate to="/blog" replace />;
  }

  const article = localizeArticle(rawArticle, lang);
  const articleList = localizeArticleList(
    Array.isArray(blogArticleList) ? blogArticleList : [],
    lang,
  );
  const ctaPath = article.cta?.path || "/tours/one-day-photogenic-tour";

  const popularArticles = articleList
    .filter((item) => item.slug !== article.slug)
    .slice(0, 4);
  const relatedGuideLinks = getBlogGuideLinks(slug);

  return (
    <>
      <Header logoColor="dark" />

      <main className={styles.root}>
        <div className={styles.pageLayout}>
          <article className={styles.article}>
            <ArticleHeader article={article} />
            {slug === "miyako-hotel-guide-by-area" && (
              <Link to="/ocean-guide/hotels" className={styles.hotelGuideLink} onClick={() => trackCtaClick({ label: "hotel_guide", placement: "hotel_blog", destination: "/ocean-guide/hotels", destinationType: "internal" })}>
                <span><strong>旅に合うホテルを、もっと探しやすく</strong><small>エリア・予算で絞り込み、保存した宿を比較できます</small></span><span aria-hidden="true">→</span>
              </Link>
            )}

            <div className={styles.mainVisual}>
              <BlogMedia
                media={
                  article.mainInstagramUrl
                    ? {
                        instagramUrl: article.mainInstagramUrl,
                      }
                    : article.mainMedia || article.mainImage
                }
                alt={article.title}
              />
            </div>

            <TableOfContents article={article} />

            <section className={styles.contentSection}>
              <ArticleContent article={article} />

              {lang === "JP" && (
                <aside className={styles.relatedGuides} aria-labelledby="related-ocean-guides">
                  <p>OCEAN GUIDE</p>
                  <h2 id="related-ocean-guides">この記事と一緒に確認したい情報</h2>
                  <div>{relatedGuideLinks.map(([path, label]) => <Link key={path} to={path}>{label}<span aria-hidden="true">→</span></Link>)}</div>
                </aside>
              )}

              <Link
                className={styles.moreButton}
                to="/blog"
                onClick={() =>
                  trackCtaClick({
                    label: "back_to_blog",
                    placement: "blog_article",
                    destination: "/blog",
                    destinationType: "internal",
                  })
                }
              >
                {t.backToBlog}
              </Link>
            </section>

            <BlogMemoryCta to={ctaPath} cta={article.cta} />
          </article>

          <Sidebar popularArticles={popularArticles} />
        </div>
      </main>

      <Footer />
    </>
  );
}
